import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import StarIcon from "@material-ui/icons/Star";
import { authDelete, authGet, authPatch, AuthRequest } from "../../lib";
import { REACT_APP_API_URL } from "../../config";
import { PREOWNED_PT_FEATURES } from "../../themes";

/**
 * Served by the admin backend from PREOWNED_IMAGES_DIR, not by the storefront —
 * an upload is visible the moment it lands, and the gallery doesn't depend on
 * the storefront having the same files. In production both point at the same
 * directory, so the two stay identical.
 *
 * REACT_APP_API_URL carries an /api/V1 suffix in .env.development (axios uses
 * it as baseURL) but not in .env. The static mount is at the server root, so
 * the suffix is stripped rather than assumed either way.
 */
const API_ORIGIN = String(REACT_APP_API_URL || "").replace(/\/api\/v\d+\/?$/i, "");
const IMG_BASE = `${API_ORIGIN}/preowned-images`;
/** watchfinder/ and gbase/ are flat sibling mounts, not per-item folders. */
const IMG_BASE_ROOT = `${API_ORIGIN}/preowned-images`;

const useStyles = makeStyles((theme) => ({
  bar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  grid: { display: "flex", flexWrap: "wrap", gap: theme.spacing(1.5) },
  wmLabel: { fontSize: 12, fontWeight: 600 },
  wmOption: { fontSize: 12, display: "flex", alignItems: "center", marginRight: 4 },
  wmHint: { fontSize: 11, color: theme.palette.warning.dark },
  rowHead: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.75),
  },
  rowTitle: { fontSize: 12, fontWeight: 700 },
  rowNote: { fontSize: 11, color: theme.palette.text.secondary },
  card: {
    width: 150,
    border: "1px solid rgba(0,0,0,0.12)",
    borderRadius: 3,
    background: "#fff",
    overflow: "hidden",
  },
  thumbWrap: {
    height: 150,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#fafafa",
  },
  thumb: { maxWidth: "100%", maxHeight: "100%", display: "block" },
  // Images restored from a prod dump have rows but no local file.
  missing: { fontSize: 10, color: "rgba(0,0,0,0.35)", textAlign: "center", padding: 8 },
  cardBar: {
    display: "flex",
    alignItems: "center",
    borderTop: "1px solid rgba(0,0,0,0.08)",
    padding: "0 2px",
  },
  pos: { fontSize: 11, color: theme.palette.text.secondary, marginLeft: 4 },
  error: { color: theme.palette.error.main },
}));

/**
 * Gallery for one item. The site and feed galleries hold the same files but
 * separate rows and positions, so the feed copy can diverge; only the site
 * gallery is edited here, which is what the CI4 screen does by default.
 *
 * `itemId` is null while a watch is still being added. In that state there is
 * nothing to POST to, so files are held and handed upward through `onPending`;
 * the page uploads them once the record exists.
 */
export function PreownedImages({ itemId, imageDir, onPending }) {
  const pendingMode = !itemId;
  const classes = useStyles();
  const [images, setImages] = useState([]);
  const [feedImages, setFeedImages] = useState([]);
  const [single, setSingle] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Tracked in state, not by mutating the node — an imperative display:none
  // survives re-renders React doesn't own, so a once-failed image stays hidden
  // even after the src is corrected.
  const [failed, setFailed] = useState({});
  // Files chosen before the record exists; uploaded by the page after create.
  const [pending, setPending] = useState([]);
  // PT watermarks every site image and the color is a deliberate choice, so
  // the file picker stays disabled until one is selected. TPC hasn't decided
  // whether it wants a watermark, so the whole control is PT-only.
  const [watermark, setWatermark] = useState("");
  const needsWatermark = PREOWNED_PT_FEATURES && !watermark;

  const imgSrc = (img) => `${IMG_BASE}${imageDir}/${itemId}/${img.image}`;

  const load = useCallback(async () => {
    const { data, error: err } = await authGet(`/preowned/images/${itemId}`);
    if (err) return setError(err.message || "Couldn't load images");
    setImages(data?.images ?? []);
    setFeedImages(data?.feed_images ?? []);
    setSingle({
      watchfinder: data?.watchfinder_img ?? null,
      gbase: data?.gbase_img ?? null,
    });
    setFailed({});
  }, [itemId]);

  useEffect(() => {
    if (!pendingMode) load();
  }, [load, pendingMode]);

  /**
   * `target` is "site" (both galleries, watermarked), "feed" (feed only, never
   * watermarked) or a single slot name.
   */
  async function upload(files, target = "site") {
    if (!files?.length) return;
    if (pendingMode) {
      // Preview locally and hand the files upward; nothing is sent yet. The
      // watermark choice rides along per file since the page replays these
      // uploads later, after the picker is gone.
      setPending((prev) => {
        const next = [
          ...prev,
          ...files.map((f) => ({
            file: f,
            target,
            watermark,
            url: URL.createObjectURL(f),
          })),
        ];
        onPending?.(next);
        return next;
      });
      return;
    }
    setBusy(true);
    setError("");
    const body = new FormData();
    // Text fields before files — multer parses the stream in order and a field
    // trailing the uploads isn't reliably on req.body when the handler runs.
    if (target === "site" && PREOWNED_PT_FEATURES) {
      body.append("watermark_color", watermark);
    }
    for (const f of files) body.append("afile", f);

    const url =
      target === "site"
        ? `/preowned/images/${itemId}`
        : target === "feed"
        ? `/preowned/images/${itemId}/feed`
        : `/preowned/images/${itemId}/slot/${target}`;

    try {
      // Same shape the main admin's Images.jsx uses — axios attaches the auth
      // header via its interceptor and sets the multipart boundary.
      await AuthRequest.post(url, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Upload failed");
    }
    setBusy(false);
    load();
  }

  async function removeSingle(slot) {
    if (!window.confirm(`Remove the ${slot} image?`)) return;
    setBusy(true);
    await authDelete(`/preowned/images/${itemId}/slot/${slot}`);
    setBusy(false);
    load();
  }

  async function move(image, direction) {
    setBusy(true);
    await authPatch(`/preowned/images/detail/${image.image_id}`, { direction });
    setBusy(false);
    load();
  }

  async function remove(image) {
    if (!window.confirm("Delete this image? The file is removed from disk too."))
      return;
    setBusy(true);
    const { error: err } = await authDelete(
      `/preowned/images/detail/${image.image_id}`,
    );
    setBusy(false);
    if (err) return setError(err.message || "Delete failed");
    load();
  }

  /** One gallery row: uploader on the left, its cards flowing to the right. */
  const Gallery = ({ title, note, rows, target, disabled, hint }) => (
    <Box mb={3}>
      <div className={classes.rowHead}>
        <span className={classes.rowTitle}>{title}</span>
        <span className={classes.rowNote}>{note}</span>
        <input
          type="file"
          multiple
          accept="image/*"
          disabled={disabled || busy}
          onChange={(e) => upload(Array.from(e.target.files || []), target)}
          style={{ fontSize: 12, opacity: disabled ? 0.5 : 1 }}
        />
        {hint && <span className={classes.wmHint}>{hint}</span>}
      </div>
      {!rows.length ? (
        <Typography variant="body2" color="textSecondary">
          No images yet.
        </Typography>
      ) : (
        <div className={classes.grid}>
          {rows.map((img, i) => (
            <div key={img.image_id} className={classes.card}>
              <div className={classes.thumbWrap}>
                {failed[img.image_id] ? (
                  <a
                    className={classes.missing}
                    href={imgSrc(img)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={imgSrc(img)}
                  >
                    {img.image}
                    <br />
                    (not on this server — click to check)
                  </a>
                ) : (
                  <img
                    className={classes.thumb}
                    alt={img.title || img.image}
                    src={imgSrc(img)}
                    onError={() => setFailed((f) => ({ ...f, [img.image_id]: true }))}
                  />
                )}
              </div>
              <div className={classes.cardBar}>
                <IconButton size="small" disabled={i === 0 || busy} onClick={() => move(img, "up")}>
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={i === rows.length - 1 || busy}
                  onClick={() => move(img, "down")}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
                {i === 0 ? (
                  <Chip
                    icon={<StarIcon style={{ fontSize: 13 }} />}
                    label="MAIN"
                    size="small"
                    color="primary"
                    variant="outlined"
                    style={{ height: 18, fontSize: 10, marginLeft: 2 }}
                  />
                ) : (
                  <span className={classes.pos}>{img.position}</span>
                )}
                <Box flexGrow={1} />
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => remove(img)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      )}
    </Box>
  );

  /** Single-image slot: one file, stored on the item row. */
  const SingleSlot = ({ title, note, slot, dir }) => {
    const name = single[slot];
    return (
      <Box mb={3}>
        <div className={classes.rowHead}>
          <span className={classes.rowTitle}>{title}</span>
          <span className={classes.rowNote}>{note}</span>
          <input
            type="file"
            accept="image/*"
            disabled={busy}
            onChange={(e) => upload(Array.from(e.target.files || []), slot)}
            style={{ fontSize: 12 }}
          />
        </div>
        {!name ? (
          <Typography variant="body2" color="textSecondary">
            None set.
          </Typography>
        ) : (
          <div className={classes.card} style={{ width: 150 }}>
            <div className={classes.thumbWrap}>
              {failed[slot] ? (
                <a
                  className={classes.missing}
                  href={`${IMG_BASE_ROOT}/${dir}/${name}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {name}
                  <br />
                  (not on this server)
                </a>
              ) : (
                <img
                  className={classes.thumb}
                  alt={name}
                  src={`${IMG_BASE_ROOT}/${dir}/${name}`}
                  onError={() => setFailed((f) => ({ ...f, [slot]: true }))}
                />
              )}
            </div>
            <div className={classes.cardBar}>
              <span className={classes.pos} style={{ flexGrow: 1 }}>
                {name}
              </span>
              <Tooltip title="Remove">
                <IconButton size="small" onClick={() => removeSingle(slot)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        )}
      </Box>
    );
  };

  // Before the record exists there is nothing to attach images to, so this
  // mode just collects them; the page uploads once it has an item_id.
  if (pendingMode) {
    return (
      <>
        {PREOWNED_PT_FEATURES && (
          <div className={classes.bar}>
            <span className={classes.wmLabel}>Watermark:</span>
            {["black", "white"].map((c) => (
              <label key={c} className={classes.wmOption}>
                <input
                  type="radio"
                  name="watermark"
                  value={c}
                  checked={watermark === c}
                  onChange={() => setWatermark(c)}
                  style={{ marginRight: 4, accentColor: "#1565c0" }}
                />
                {c}
              </label>
            ))}
            <span className={classes.rowNote}>
              applied to the site gallery only — feeds reject watermarked imagery
            </span>
          </div>
        )}

        <div className={classes.rowHead}>
          <span className={classes.rowTitle}>Site</span>
          <span className={classes.rowNote}>
            {needsWatermark
              ? "Pick a watermark color first"
              : "uploaded when you save this watch"}
          </span>
          <input
            type="file"
            multiple
            accept="image/*"
            disabled={needsWatermark}
            onChange={(e) => upload(Array.from(e.target.files || []), "site")}
            style={{ fontSize: 12 }}
          />
        </div>
        {!pending.length ? (
          <Typography variant="body2" color="textSecondary">
            No images selected yet.
          </Typography>
        ) : (
          <div className={classes.grid}>
            {pending.map((p, i) => (
              <div key={i} className={classes.card}>
                <div className={classes.thumbWrap}>
                  <img className={classes.thumb} alt={p.file.name} src={p.url} />
                </div>
                <div className={classes.cardBar}>
                  <span className={classes.rowNote}>{p.file.name}</span>
                  <button
                    type="button"
                    onClick={() =>
                      setPending((prev) => {
                        const next = prev.filter((_, n) => n !== i);
                        onPending?.(next);
                        return next;
                      })
                    }
                    style={{ fontSize: 11, cursor: "pointer" }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {error && (
        <Typography className={classes.error} gutterBottom>
          {error}
        </Typography>
      )}

      {PREOWNED_PT_FEATURES && (
        <div className={classes.bar}>
          <span className={classes.wmLabel}>Watermark:</span>
          {["black", "white"].map((c) => (
            <label key={c} className={classes.wmOption}>
              <input
                type="radio"
                name="watermark"
                value={c}
                checked={watermark === c}
                onChange={() => setWatermark(c)}
                style={{ marginRight: 4, accentColor: "#1565c0" }}
              />
              {c}
            </label>
          ))}
          <span className={classes.rowNote}>
            applied to the site gallery only — feeds reject watermarked imagery
          </span>
        </div>
      )}

      <Gallery
        title="Site"
        note="shown on the website; watermarked. Uploading here also fills the feed gallery."
        rows={images}
        target="site"
        disabled={needsWatermark}
        hint={needsWatermark ? "Pick a watermark color first" : ""}
      />

      <Divider style={{ margin: "12px 0" }} />

      <Gallery
        title="Feed"
        note="Google and other shopping feeds — never watermarked."
        rows={feedImages}
        target="feed"
      />

      {PREOWNED_PT_FEATURES && (
        <>
          <Divider style={{ margin: "12px 0" }} />

          <SingleSlot
            title="Watchfinder"
            note="one image, white or transparent background required."
            slot="watchfinder"
            dir="watchfinder"
          />
          <Divider style={{ margin: "12px 0" }} />

          <SingleSlot
            title="Google Base"
            note="one image."
            slot="gbase"
            dir="gbase"
          />
        </>
      )}
    </>
  );
}
export default PreownedImages;
